/**
 * Story 034 — TanStack Query hooks for `/me/preferences/lyse` (BR-097).
 *
 * Surface:
 *   - `useLysePreferencesQuery()` — fires GET on Analysis-page mount.
 *     Returns the lenient-parsed `LysePreferences` (factory defaults
 *     filled in for missing keys). 404 resolves to defaults — NOT an
 *     error.
 *   - `useUpdateLysePreferencesMutation()` — returns an `update(patch)`
 *     callback. Coalesces rapid updates via a 500ms debounce; fires
 *     one PATCH carrying the merged diff.
 *   - `useResetLysePreferencesMutation()` — DELETEs the row and resets
 *     the in-memory snapshot to factory defaults.
 *
 * Cross-device parity is the side benefit of going through the server
 * (vs localStorage). The hook does NOT shadow the result in
 * localStorage by default — story §"Out of scope" for cross-device
 * consistency.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useAuthStore } from "@aeriosiq/auth-client";
import { authFetch, type AuthFetchError } from "./authFetch.js";
import {
  factoryDefaults,
  parseLysePreferences,
  type LysePreferences,
} from "./schema.js";

const PATH = "/me/preferences/lyse";
const DEBOUNCE_MS = 500;

/**
 * Shallow-merge two pending patch objects, preserving peer keys on the
 * nested object fields (`colorToggles`, `widgetVisibility`,
 * `favouriteCatalogueEntries`, `layout`). Top-level scalars (sort,
 * imageStateFilter) are last-write-wins.
 */
function mergePatch(
  prev: LysePreferencesPatch,
  next: LysePreferencesPatch,
): LysePreferencesPatch {
  const out: LysePreferencesPatch = { ...prev, ...next };
  if (prev.colorToggles || next.colorToggles) {
    out.colorToggles = {
      ...(prev.colorToggles ?? {}),
      ...(next.colorToggles ?? {}),
    };
  }
  if (prev.widgetVisibility || next.widgetVisibility) {
    out.widgetVisibility = {
      ...(prev.widgetVisibility ?? {}),
      ...(next.widgetVisibility ?? {}),
    };
  }
  if (prev.favouriteCatalogueEntries || next.favouriteCatalogueEntries) {
    out.favouriteCatalogueEntries = {
      ...(prev.favouriteCatalogueEntries ?? {}),
      ...(next.favouriteCatalogueEntries ?? {}),
    };
  }
  if (prev.layout || next.layout) {
    out.layout = { ...(prev.layout ?? {}), ...(next.layout ?? {}) };
  }
  // R58-L-17 — viewer prefs (maxZoomPercent, showDebugOverlay) coalesce
  // identically to colorToggles: peer keys preserved on the client side,
  // wire-side semantics still last-write-wins (the FE always sends the
  // FULL viewer block, never a partial — see ViewerSettings.tsx setter).
  if (prev.viewer || next.viewer) {
    out.viewer = { ...(prev.viewer ?? {}), ...(next.viewer ?? {}) };
  }
  // ANA-R3-TILE-03 — tiles prefs (maxZoomPercent, tileSize) coalesce
  // identically to viewer: peer keys preserved client-side; the FE always
  // sends the full `tiles` block (see useTileSettings.ts setters).
  if (prev.tiles || next.tiles) {
    out.tiles = { ...(prev.tiles ?? {}), ...(next.tiles ?? {}) };
  }
  // SB6-ANA-FILT-05 — globalDataPointFilter is replaced wholesale (the
  // filter widget always sends the full 7-axis selection), so the
  // top-level last-write-wins from the spread above is correct.
  // globalDataPointFilterCountUnit is a scalar, also last-write-wins.
  return out;
}

export const lysePreferencesKeys = {
  all: ["lyse", "preferences"] as const,
  current: () => [...lysePreferencesKeys.all, "current"] as const,
};

interface RawQueryResult {
  prefs: LysePreferences;
  isFromServer: boolean;
  isFetchError: boolean;
}

async function fetchPrefs(): Promise<RawQueryResult> {
  try {
    const raw = await authFetch<unknown>(PATH);
    if (raw === undefined || raw === null) {
      return {
        prefs: factoryDefaults(),
        isFromServer: false,
        isFetchError: false,
      };
    }
    return {
      prefs: parseLysePreferences(raw),
      isFromServer: true,
      isFetchError: false,
    };
  } catch (err) {
    const fetchErr = err as AuthFetchError;
    if (typeof fetchErr.status === "number" && fetchErr.status === 404) {
      return {
        prefs: factoryDefaults(),
        isFromServer: false,
        isFetchError: false,
      };
    }
    if (typeof fetchErr.status === "number" && fetchErr.status >= 500) {
      return {
        prefs: factoryDefaults(),
        isFromServer: false,
        isFetchError: true,
      };
    }
    // 4xx other than 404 — bubble to React Query's error path so DevTools
    // catches the developer mistake (e.g. missing tenant header).
    throw err;
  }
}

export interface LysePreferencesQueryResult {
  prefs: LysePreferences;
  isFromServer: boolean;
  isFetchError: boolean;
  isPending: boolean;
}

export function useLysePreferencesQuery(): LysePreferencesQueryResult {
  // 2026-06-04 — on a deep-link boot this fired BEFORE tenant-from-host
  // resolved (no X-Tenant-ID yet) → a 400 error toast on every fresh
  // analysis-page load. Gate on the resolved tenant; while gated the hook
  // serves factory defaults exactly like the in-flight case below.
  const tenantReady = useAuthStore((s) => s.tenant != null);
  const query = useQuery({
    queryKey: lysePreferencesKeys.current(),
    queryFn: fetchPrefs,
    enabled: tenantReady,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  // While the GET is in flight, hand callers factory defaults so the
  // UI renders without a flicker. The bridge below also uses this
  // path — `prefs` is always a usable `LysePreferences`.
  const data = query.data ?? {
    prefs: factoryDefaults(),
    isFromServer: false,
    isFetchError: false,
  };
  return {
    prefs: data.prefs,
    isFromServer: data.isFromServer,
    isFetchError: data.isFetchError,
    isPending: query.isPending,
  };
}

async function patchPrefs(patch: LysePreferencesPatch): Promise<void> {
  await authFetch<undefined>(PATH, { method: "PATCH", body: patch });
}

async function deletePrefs(): Promise<void> {
  await authFetch<undefined>(PATH, { method: "DELETE" });
}

/**
 * Patch shape accepted by `update(...)`. Distinct from
 * `Partial<LysePreferences>` because the nested objects (`colorToggles`,
 * `widgetVisibility`) are also partial — callers can set just one key
 * at a time and the hook deep-merges with the cached value.
 */
export interface LysePreferencesPatch {
  sortPreference?: LysePreferences["sortPreference"];
  widgetVisibility?: Record<string, boolean>;
  colorToggles?: Partial<LysePreferences["colorToggles"]>;
  favouriteCatalogueEntries?: Record<string, string[]>;
  imageStateFilter?: LysePreferences["imageStateFilter"];
  layout?: Record<string, unknown>;
  // R58-L-17 — Viewer settings panel writes the full `viewer` block on
  // every change (no partial PATCH from the slider/toggle); the
  // `Partial<>` here is purely defensive against caller mistakes.
  viewer?: Partial<LysePreferences["viewer"]>;
  // ANA-R3-TILE-03 — the Tiles settings section writes the full `tiles`
  // block on every change (no partial PATCH from the slider/size control);
  // the `Partial<>` here is purely defensive against caller mistakes.
  tiles?: Partial<LysePreferences["tiles"]>;
  // SETTINGS-02 — the Bounding Boxes tab always writes the FULL
  // boundingBox block (per-kind objects are replaced wholesale, matching
  // the server's top-level last-write-wins on nested objects).
  boundingBox?: LysePreferences["boundingBox"];
  // SB6-ANA-FILT-05 — Global Data Point Filter prefs. The count unit is
  // a scalar; the selection is replaced wholesale (the widget sends the
  // full 7-axis selection on every change).
  globalDataPointFilterCountUnit?: LysePreferences["globalDataPointFilterCountUnit"];
  globalDataPointFilter?: LysePreferences["globalDataPointFilter"];
}

export interface UpdateLysePreferencesApi {
  /**
   * Debounced patch. Multiple calls within 500 ms are merged and only
   * the final state lands on the wire.
   */
  update: (patch: LysePreferencesPatch) => void;
  /** Force-flush the pending debounce. */
  flush: () => void;
  isSaving: boolean;
}

/**
 * Returns the imperative `update` API. Lives at the AnalysisFeature
 * (or any other) host; the Settings drawer + image-state filter both
 * call `update(diff)` on every gesture.
 */
export function useUpdateLysePreferencesMutation(): UpdateLysePreferencesApi {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: patchPrefs,
  });

  // Pending state — accumulates partial patches between debounce flushes.
  // We send the accumulated diff (not the merged snapshot) so the server
  // can decide its own merge semantics; the optimistic cache update above
  // already holds the merged value.
  const pendingRef = useRef<LysePreferencesPatch>({});
  const timerRef = useRef<number | null>(null);

  const flushNow = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const diff = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(diff).length === 0) return;
    mutation.mutate(diff);
  }, [mutation]);

  const update = useCallback(
    (patch: LysePreferencesPatch) => {
      // Optimistically reflect each individual patch in the cache so
      // the UI updates between debounce flushes. We `setQueryData`
      // directly here (instead of relying on mutation.onMutate) so the
      // cache update lands BEFORE the debounce window opens — the
      // Settings drawer's checkboxes read from the cached snapshot and
      // would otherwise flicker.
      const prev = qc.getQueryData<RawQueryResult>(
        lysePreferencesKeys.current(),
      );
      const prevPrefs: LysePreferences = prev?.prefs ?? factoryDefaults();
      const merged: LysePreferences = {
        ...prevPrefs,
        sortPreference: patch.sortPreference ?? prevPrefs.sortPreference,
        imageStateFilter: patch.imageStateFilter ?? prevPrefs.imageStateFilter,
        layout: patch.layout
          ? { ...prevPrefs.layout, ...patch.layout }
          : prevPrefs.layout,
        favouriteCatalogueEntries: patch.favouriteCatalogueEntries
          ? {
              ...prevPrefs.favouriteCatalogueEntries,
              ...patch.favouriteCatalogueEntries,
            }
          : prevPrefs.favouriteCatalogueEntries,
        colorToggles: patch.colorToggles
          ? { ...prevPrefs.colorToggles, ...patch.colorToggles }
          : prevPrefs.colorToggles,
        widgetVisibility: patch.widgetVisibility
          ? { ...prevPrefs.widgetVisibility, ...patch.widgetVisibility }
          : prevPrefs.widgetVisibility,
        // R58-L-17 — viewer settings (maxZoomPercent + showDebugOverlay).
        // Same shallow-merge rule as colorToggles: peer keys preserved
        // on the client side so a single-field update (e.g. only the
        // debug-overlay toggle) doesn't drop maxZoomPercent from the
        // cached snapshot.
        viewer: patch.viewer
          ? { ...prevPrefs.viewer, ...patch.viewer }
          : prevPrefs.viewer,
        // ANA-R3-TILE-03 — same shallow-merge as viewer so a single-field
        // tiles update keeps the peer key in the cached snapshot.
        tiles: patch.tiles
          ? { ...prevPrefs.tiles, ...patch.tiles }
          : prevPrefs.tiles,
        // SETTINGS-02 — full-block replace (the tab sends the whole
        // boundingBox object on every change).
        boundingBox: patch.boundingBox ?? prevPrefs.boundingBox,
        // SB6-ANA-FILT-05 — count unit (scalar) + selection (full-block
        // replace). `globalDataPointFilter` may be set to `null` to mean
        // "no persisted selection"; we therefore use `in`-checking via
        // `??` only when the key is absent from the patch.
        globalDataPointFilterCountUnit:
          patch.globalDataPointFilterCountUnit ??
          prevPrefs.globalDataPointFilterCountUnit,
        globalDataPointFilter:
          "globalDataPointFilter" in patch
            ? (patch.globalDataPointFilter ?? null)
            : prevPrefs.globalDataPointFilter,
      };
      qc.setQueryData<RawQueryResult>(lysePreferencesKeys.current(), {
        prefs: merged,
        isFromServer: prev?.isFromServer ?? false,
        isFetchError: false,
      });
      // Merge into the pending diff. Object fields are merged shallowly
      // (peer keys are preserved) so a sequence like
      // update({colorToggles:{a:true}}) → update({colorToggles:{b:false}})
      // sends `{ colorToggles: { a: true, b: false } }` on the wire.
      pendingRef.current = mergePatch(pendingRef.current, patch);
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        const diff = pendingRef.current;
        pendingRef.current = {};
        if (Object.keys(diff).length === 0) return;
        mutation.mutate(diff);
      }, DEBOUNCE_MS);
    },
    [mutation, qc],
  );

  // Flush on unmount so a navigation right after a last-millisecond
  // toggle doesn't drop the gesture on the floor.
  useEffect(() => {
    return () => {
      flushNow();
    };
  }, [flushNow]);

  return {
    update,
    flush: flushNow,
    isSaving: mutation.isPending,
  };
}

/**
 * Imperative reset — DELETE the row and seed the cache with factory
 * defaults. Returned as a function so the Settings drawer can `await`
 * it (the e2e spec needs the call to settle before asserting the
 * cleared state).
 */
export function useResetLysePreferencesMutation(): {
  reset: () => Promise<void>;
  isResetting: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: deletePrefs,
    onSuccess: () => {
      qc.setQueryData<RawQueryResult>(lysePreferencesKeys.current(), {
        prefs: factoryDefaults(),
        isFromServer: false,
        isFetchError: false,
      });
    },
  });
  const reset = useCallback(async () => {
    await mutation.mutateAsync();
  }, [mutation]);
  return { reset, isResetting: mutation.isPending };
}
